import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Linkedin, Loader2, RefreshCw, Bot, CheckCircle2, Clock, Search, CalendarDays } from 'lucide-react';
import api from '../lib/api';
import { formatLocal } from '../lib/dateUtils';
import LinkedInThreadView from '../components/LinkedInThreadView';

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function DatePickerButton({ value, onChange, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs font-normal justify-start w-[140px]" data-testid={testid}>
          <CalendarDays className="h-3.5 w-3.5 mr-1.5 opacity-70" />
          {value ? value : <span className="text-muted-foreground">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ? new Date(`${value}T00:00:00`) : undefined}
          onSelect={(d) => { if (d) onChange(toISO(d)); setOpen(false); }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${active ? 'border-sky-500/60 bg-sky-500/15 text-sky-500' : 'border-border text-muted-foreground hover:text-foreground'}`}
    >
      {children}
    </button>
  );
}

function ConvRow({ conv, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-3 border-b border-border/40 transition-colors hover:bg-muted/40 ${active ? 'bg-muted/60' : ''}`}
      data-testid={`li-conv-${conv.lead_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm truncate">{conv.lead_name}</span>
        <span className="text-[10px] text-muted-foreground shrink-0">{conv.last_ts ? formatLocal(conv.last_ts) : ''}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        {conv.last_direction === 'inbound' && <Bot className="h-3 w-3 text-sky-500 shrink-0" />}
        <p className="text-xs text-muted-foreground truncate flex-1">{conv.last_message || '—'}</p>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        {conv.connected && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-500/40 text-emerald-500">
            <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Connected
          </Badge>
        )}
        {!conv.connected && conv.linkedin_status === 'pending' && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/40 text-amber-500">
            <Clock className="h-2.5 w-2.5 mr-0.5" />Pending
          </Badge>
        )}
        {conv.awaiting_reply && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-sky-500/40 text-sky-400">Needs reply</Badge>
        )}
      </div>
    </button>
  );
}

export default function LinkedInConversations({ embedded = false }) {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [search, setSearch] = useState('');

  const loadConvs = useCallback(async () => {
    try {
      const res = await api.get('/api/linkedin/conversations');
      setConvs(res.data?.conversations || []);
    } catch (e) {
      console.error('Failed to load LinkedIn conversations', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  // Deep-link: ?lead=<id> auto-selects that conversation.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const leadParam = params.get('lead');
    if (leadParam && !selected) {
      const match = convs.find((c) => c.lead_id === leadParam);
      setSelected(match || { lead_id: leadParam, lead_name: 'Lead', linkedin_url: null });
    }
  }, [loading, convs, selected]);

  // Refresh the list every 30s while visible.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') loadConvs();
    }, 30000);
    return () => clearInterval(timer);
  }, [loadConvs]);

  const matchDate = (ts) => {
    if (dateFilter === 'all' || !ts) return true;
    const d = new Date(ts).getTime();
    if (Number.isNaN(d)) return true;
    if (dateFilter === 'custom') {
      if (customFrom) { const f = new Date(customFrom); f.setHours(0, 0, 0, 0); if (d < f.getTime()) return false; }
      if (customTo) { const t = new Date(customTo); t.setHours(23, 59, 59, 999); if (d > t.getTime()) return false; }
      return true;
    }
    if (dateFilter === 'today') { const t = new Date(); t.setHours(0, 0, 0, 0); return d >= t.getTime(); }
    const days = dateFilter === '7d' ? 7 : 30;
    return d >= Date.now() - days * 86400000;
  };
  const matchStatus = (c) => {
    if (statusFilter === 'connected') return !!c.connected;
    if (statusFilter === 'pending') return !c.connected && c.linkedin_status === 'pending';
    if (statusFilter === 'not_connected') return !c.connected && c.linkedin_status !== 'pending';
    if (statusFilter === 'needs_reply') return !!c.awaiting_reply;
    return true;
  };
  const q = search.trim().toLowerCase();
  const filteredConvs = convs.filter((c) => matchStatus(c) && matchDate(c.last_ts)
    && (!q || `${c.lead_name || ''}`.toLowerCase().includes(q)));

  return (
    <div className="space-y-4" data-testid="linkedin-conversations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        {!embedded ? (
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Linkedin className="h-6 w-6 text-[#0a66c2]" />LinkedIn Conversations
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Every connection request, note & reply per lead in one thread</p>
          </div>
        ) : <div />}
        <Button variant="outline" size="sm" onClick={loadConvs} data-testid="li-refresh-convs">
          <RefreshCw className="h-4 w-4 mr-1" />Refresh
        </Button>
      </div>

      {/* Filter toolbar — kept above the inbox for a clean, uncluttered list */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card/40 px-3 py-2" data-testid="li-filter-bar">
        <div className="relative w-full sm:w-64">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name"
            className="h-8 text-xs pl-8"
            data-testid="li-filter-search"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1" data-testid="li-filter-status">
          {[
            { k: 'all', label: 'All', n: convs.length },
            { k: 'connected', label: 'Connected', n: convs.filter((c) => c.connected).length },
            { k: 'pending', label: 'Pending', n: convs.filter((c) => !c.connected && c.linkedin_status === 'pending').length },
            { k: 'not_connected', label: 'Not connected', n: convs.filter((c) => !c.connected && c.linkedin_status !== 'pending').length },
            { k: 'needs_reply', label: 'Needs reply', n: convs.filter((c) => c.awaiting_reply).length },
          ].map((t) => (
            <FilterPill key={t.k} active={statusFilter === t.k} onClick={() => setStatusFilter(t.k)} testid={`li-filter-${t.k}`}>
              {t.label}{t.n ? ` ${t.n}` : ''}
            </FilterPill>
          ))}
        </div>
        <div className="sm:ml-auto flex flex-wrap items-center gap-1" data-testid="li-filter-date">
          {[{ k: 'all', label: 'All' }, { k: 'today', label: 'Today' }, { k: '7d', label: '7d' }, { k: '30d', label: '30d' }, { k: 'custom', label: 'Custom' }].map((t) => (
            <FilterPill key={t.k} active={dateFilter === t.k} onClick={() => setDateFilter(t.k)} testid={`li-date-${t.k}`}>{t.label}</FilterPill>
          ))}
          {dateFilter === 'custom' && (
            <div className="flex items-center gap-1" data-testid="li-custom-date">
              <DatePickerButton value={customFrom} onChange={setCustomFrom} placeholder="From" testid="li-custom-from" />
              <span className="text-xs text-muted-foreground">to</span>
              <DatePickerButton value={customTo} onChange={setCustomTo} placeholder="To" testid="li-custom-to" />
            </div>
          )}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] h-[600px]">
          {/* Left: conversation list */}
          <div className="border-r border-border overflow-y-auto min-h-0" data-testid="li-conv-list">
            {loading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : convs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Linkedin className="h-8 w-8 mx-auto mb-2 opacity-30" />
                No LinkedIn conversations yet
              </div>
            ) : filteredConvs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="li-no-match">
                No conversations match these filters.
              </div>
            ) : (
              filteredConvs.map((c) => (
                <ConvRow key={c.lead_id} conv={c} active={selected?.lead_id === c.lead_id} onClick={() => setSelected(c)} />
              ))
            )}
          </div>

          {/* Right: thread */}
          <div className="flex flex-col min-h-0 overflow-hidden">
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Linkedin className="h-10 w-10 opacity-30 mb-2" />
                <p className="text-sm">Select a conversation</p>
              </div>
            ) : (
              <LinkedInThreadView
                key={selected.lead_id}
                leadId={selected.lead_id}
                leadName={selected.lead_name}
                linkedinUrl={selected.linkedin_url}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
